import { AttachmentListControls } from '@/components/attachment-list/attachment-list-controls';
import { AttachmentListImage } from '@/components/attachment-list/attachment-list-image';
import { CursorValue, StyleSheet, View } from 'react-native';
import { type FC, useRef } from 'react';
import { ZoomableImageMapProps } from './types';

export const ZoomableImageMap: FC<ZoomableImageMapProps> = ({
  containerWidth,
  images,
  onAreaSelect,
  selectedAreaID,
  setZoomLevel,
  zoomControlsBottomOffset,
  zoomLevel = 1,
}) => {
  const isDraggingRef = useRef(false);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const startDrag = (e: React.MouseEvent) => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = true;
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
      scrollContainerRef.current.style.cursor = 'grabbing';
    }
  };

  const stopDrag = () => {
    if (scrollContainerRef.current) {
      isDraggingRef.current = false;
      scrollContainerRef.current.style.cursor = 'grab';
    }
  };

  return (
    <View style={styles.container}>
      <div
        onMouseDown={startDrag}
        onMouseLeave={stopDrag}
        onMouseMove={(e) => {
          if (isDraggingRef.current && scrollContainerRef.current) {
            const dx = e.clientX - lastPositionRef.current.x;
            const dy = e.clientY - lastPositionRef.current.y;

            scrollContainerRef.current.scrollLeft -= dx;
            scrollContainerRef.current.scrollTop -= dy;

            lastPositionRef.current = { x: e.clientX, y: e.clientY };
          }
        }}
        onMouseUp={stopDrag}
        ref={scrollContainerRef}
        style={styles.scrollContainer}
      >
        <View
          style={{
            height: `${100 * zoomLevel}%`,
            width: `${100 * zoomLevel}%`,
          }}
        >
          {images.map((image) => (
            <AttachmentListImage
              containerStyle={styles.attachmentListImage}
              imageMapAreas={image.areas}
              imageURL={image.url}
              key={image.url}
              onAreaSelect={onAreaSelect}
              selectedAreaID={selectedAreaID}
              width={containerWidth}
              zoomLevel={zoomLevel}
            />
          ))}
        </View>
      </div>
      {setZoomLevel ? (
        <AttachmentListControls
          containerStyle={
            zoomControlsBottomOffset
              ? { bottom: zoomControlsBottomOffset }
              : undefined
          }
          imageCount={1}
          imageIndex={0}
          setImageIndex={() => {}}
          setZoomLevel={(nextZoomLevel) => {
            setZoomLevel(nextZoomLevel);

            if (scrollContainerRef?.current) {
              const container = scrollContainerRef.current;
              const scaleFactor = nextZoomLevel / zoomLevel;

              const centerX = container.scrollLeft + container.clientWidth / 2;
              const centerY = container.scrollTop + container.clientHeight / 2;

              requestAnimationFrame(() => {
                if (scrollContainerRef?.current) {
                  scrollContainerRef.current.scrollLeft =
                    centerX * scaleFactor - container.clientWidth / 2;
                  scrollContainerRef.current.scrollTop =
                    centerY * scaleFactor - container.clientHeight / 2;
                }
              });
            }
          }}
          zoomLevel={zoomLevel}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  attachmentListImage: {
    position: 'relative',
  },
  container: {
    flex: 1,
    position: 'relative',
    userSelect: 'none',
  },
  scrollContainer: {
    bottom: 0,
    cursor: 'grab' as CursorValue,
    display: 'flex',
    left: 0,
    overflow: 'scroll',
    position: 'absolute',
    right: 0,
    top: 0,
  },
});
